import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";
import authService from "../../../api/auth/authService";

interface CurrentUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  roleName: string;
  orgId?: string;
  orgName?: string;
  [key: string]: any;
}

interface CurrentUserState {
  data: CurrentUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  error: string | null;
}

// Decode user info from the stored JWT token
export const fetchCurrentUser = createAsyncThunk(
  "currentUser/fetch",
  async (_, { rejectWithValue }) => {
    try {
      const token = authService.getStoredToken();
      if (!token) {
        return rejectWithValue("No token found");
      }

      const decoded: any = jwtDecode(token);

      // Names from JWT claims; if absent, derive from the email local-part
      // (e.g. "vara.prasad@..." → Vara / Prasad) so the avatar + greeting work.
      let firstName = decoded.firstName || decoded.first_name || decoded.name?.split(" ")[0] || "";
      let lastName = decoded.lastName || decoded.last_name || decoded.name?.split(" ")[1] || "";
      if (!firstName && decoded.email) {
        const local = String(decoded.email).split("@")[0];
        const parts = local.split(/[._\-]+/).filter(Boolean);
        const cap = (s: string) => s ? s[0].toUpperCase() + s.slice(1) : "";
        firstName = cap(parts[0] || "");
        lastName = cap(parts[1] || "");
      }

      // Build user data from JWT claims
      const user: CurrentUser = {
        id: decoded.userId || decoded.sub || decoded.id || "",
        firstName,
        lastName,
        email: decoded.email || "",
        roleName: decoded.roleName || decoded.role || decoded.roles?.[0] || "",
        orgId: decoded.orgId || decoded.org_id || "",
        orgName: decoded.orgName || decoded.org_name || "",
      };

      // Persist for quick hydration on reload
      localStorage.setItem("currentUser", JSON.stringify(user));
      return user;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to decode user token");
    }
  }
);

// Hydrate from localStorage on app boot (synchronous)
function getInitialState(): CurrentUserState {
  const storedUser = localStorage.getItem("currentUser");
  const isAuth = authService.isAuthenticated();

  if (storedUser && isAuth) {
    try {
      return {
        data: JSON.parse(storedUser),
        loading: false,
        isAuthenticated: true,
        error: null,
      };
    } catch {
      // Corrupted data, fall through
    }
  }

  return {
    data: null,
    loading: false,
    isAuthenticated: isAuth,
    error: null,
  };
}

const currentUserSlice = createSlice({
  name: "currentUser",
  initialState: getInitialState(),
  reducers: {
    logout: (state) => {
      state.data = null;
      state.isAuthenticated = false;
      state.error = null;
      localStorage.removeItem("access-cookie");
      localStorage.removeItem("currentUser");
    },
    setAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    /** merge fields from the saved profile (names / phone) so the header updates at once */
    mergeProfile: (state, action: PayloadAction<Record<string, any>>) => {
      if (!state.data) return;
      state.data = { ...state.data, ...action.payload };
      localStorage.setItem("currentUser", JSON.stringify(state.data));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
        state.data = null;
      });
  },
});

export const { logout, setAuthenticated, mergeProfile } = currentUserSlice.actions;
export default currentUserSlice.reducer;
